/**
 * @file Entrypoint for surface page.
 */
import { configure as configureArvo } from '@padlet/arvo'
import { setOauthToken as setJqueryAjaxOauthToken } from 'bits/ajax'
import { apolloProvider } from 'bits/apollo'
import { getCable } from 'bits/cable'
import { observeConnectivity } from 'bits/connectivity'
import * as errorTracker from 'bits/error_tracker'
import { captureMessage } from 'bits/error_tracker'
import fixMobileViewport from 'bits/fix_mobile_viewport'
import window, { ww } from 'bits/global'
import { captureOnlyWhitelistedClicks, initEventTracking, recordWallVisit } from 'bits/pepin'
import { setTimezoneCookie } from 'bits/timezone'
import { install as installNativeBridge } from 'native_bridge/install'
import { configureApiFetch } from 'surface/api_fetch'
import { watchHash } from 'surface/location'
import { trackPrinting } from 'surface/printing'
import { updateStoreFromSWMessage } from 'surface/service_worker_message'
import Vue, { CreateElement, VNode } from 'vue'
import VueApollo from 'vue-apollo'
import SurfaceApp from 'vuecomponents/surface_app.vue'
import store, { blankState } from 'vuexstore/surface/index'
import initialState from 'vuexstore/surface/initial_state'
import { RootState } from 'vuexstore/surface/types'

setTimezoneCookie()

// Configure Arvo
// Technically, configureArvo returns a Promise which should
// be awaited.
const {
  constants: { arvoConfig, useArvo },
} = ww.vueStartingState

setJqueryAjaxOauthToken(arvoConfig?.token?.oauthToken)

try {
  configureArvo(arvoConfig)
} catch (e) {
  const context = {
    error: e,
    vueStartingState: ww.vueStartingState,
  }
  captureMessage('Fail to configure arvo', { context })
}

try {
  configureApiFetch({ useArvo })
} catch (e) {
  const context = {
    error: e,
    vueStartingState: ww.vueStartingState,
  }
  captureMessage('Fail to configure API Fetch', { context })
}

const startingState: RootState = {
  ...blankState(),
  ...initialState(),
  ...ww.vueStartingState,
}
store.dispatch('initializeState', startingState)
store.dispatch('realtime/connect', getCable())
Vue.use(VueApollo)

errorTracker.init({
  vue: Vue,
  environment: store.state.environment,
  userId: store.state.user.id,
  locale: store.state.locale,
  release: store.state.release,
})

initEventTracking({
  environment: store.state.environment,
  user: store.state.user,
  wall: store.state.wall,
  country: store.state.constants.currentCountry,
})
captureOnlyWhitelistedClicks()

// We implement stale-while-revalidate with our service worker
// when fresh data from server arrives and the cache is updated, it will notify clients
if (navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener('message', event => {
    updateStoreFromSWMessage(event, ww.vueStartingState.wall.id, store)
  })
}

document.addEventListener('DOMContentLoaded', (): void => {
  // Mobile browsers handle 100% height in ways we don't like. Fix that.
  fixMobileViewport()
  recordWallVisit()
  // Track when user tries to print document using Ctrl+P
  trackPrinting()

  // Perform actions based on location hash, e.g. open panels
  watchHash()

  // Inform state when the browser goes offline/online
  observeConnectivity((isOnline: boolean): void => {
    store.dispatch('changeConnectivity', { isOnline })
  })

  // install older native brige methods in the window object
  installNativeBridge(window, store)

  Vue.config.devtools = process.env.NODE_ENV !== 'production'
  Vue.config.performance = process.env.NODE_ENV !== 'production'
  Vue.config.ignoredElements = ['trix-editor']

  // VueJS components
  window.app = new Vue({
    el: '#app',
    apolloProvider,
    store,
    render: (h: CreateElement): VNode => h(SurfaceApp),
  })
})
